/**
 * Base Error Boundary
 * Shared error handling logic for all error boundaries
 * Eliminates duplication between ErrorBoundary and SectionErrorBoundary
 */

import { Component, ReactNode } from 'react';

export interface BaseErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface BaseProps {
  children: ReactNode;
}

export abstract class BaseErrorBoundary<P extends BaseProps = BaseProps> extends Component<
  P,
  BaseErrorBoundaryState
> {
  constructor(props: P) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): BaseErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.logError(error, errorInfo);
  }

  /**
   * Override this method to customize error logging
   */
  protected abstract logError(error: Error, errorInfo: React.ErrorInfo): void;

  /**
   * Override this method to customize error UI
   */
  protected abstract renderError(): ReactNode;

  /**
   * Reset error state
   */
  protected resetError = (): void => {
    this.setState({ hasError: false, error: null });
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return this.renderError();
    }

    return this.props.children;
  }
}
